/**
 * Profile and circuit statistics screens (SPEC §13). Pure rendering into a
 * container; navigation is handled by callbacks so main.ts owns the routing.
 */

import type { CarConfig, CircuitStats, Livery, ProfileData } from '@microligue/shared';
import { applyLiveryToCarConfig, defaultCarConfig } from '@microligue/shared';
import { carSwatch } from '../car/svg';
import { drawLapChart } from './chart';
import { formatLapTime } from './hud';

/** SQLite UTC 'YYYY-MM-DD HH:MM:SS' → local short French date. */
export function formatDate(sqlDate: string | null): string {
  if (!sqlDate) return '—';
  const iso = sqlDate.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return sqlDate;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatGap(ms: number | null): string {
  if (ms === null) return '—';
  if (ms === 0) return 'record';
  return `+${(ms / 1000).toFixed(3).replace('.', ',')} s`;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function table(headers: string[], rows: (string | HTMLElement)[][]): HTMLElement {
  const wrap = el('div', 'stats-table-wrap');
  const t = el('table', 'stats-table');
  const head = el('tr');
  for (const h of headers) head.appendChild(el('th', undefined, h));
  t.appendChild(head);
  for (const r of rows) {
    const tr = el('tr');
    for (const cell of r) {
      const td = el('td');
      if (typeof cell === 'string') td.textContent = cell;
      else td.appendChild(cell);
      tr.appendChild(td);
    }
    t.appendChild(tr);
  }
  wrap.appendChild(t);
  return wrap;
}

export interface StatsCallbacks {
  onOpenCircuit: (circuitId: string) => void;
  onOpenProfile: (pseudo: string) => void;
}

/** Present only on the viewer's OWN profile: lets them equip a unlocked livery. */
export interface LiveryControls {
  worn: Livery | null;
  /** The player's saved bumper-car design — the wardrobe previews are this
   * design re-painted by each livery (exactly what the room will show). */
  baseDesign: CarConfig;
  onEquip: (livery: Livery | null) => void;
}

/** Titles won (palmarès) + — on one's own profile — the livery wardrobe. */
function renderTitles(
  container: HTMLElement,
  profile: ProfileData,
  liveryControls?: LiveryControls,
): void {
  container.appendChild(el('h3', undefined, 'Titres & palmarès'));
  if (profile.titles.length === 0) {
    container.appendChild(
      el('div', 'stats-empty', 'Aucun titre — gagnez une saison de ligue pour en décrocher un.'),
    );
  } else {
    const list = el('div', 'title-list');
    for (const t of profile.titles) {
      const card = el('div', `title-card${t.active ? '' : ' expired'}`);
      if (t.livery) {
        // Own profile: the livery on YOUR design. Someone else's: on a neutral
        // grey car (their raw design is not public — only the merged room look).
        const base = liveryControls?.baseDesign ?? defaultCarConfig('#888888');
        card.appendChild(carSwatch(applyLiveryToCarConfig(base, t.livery), 44));
      }
      const body = el('div', 'title-body');
      body.appendChild(el('div', 'title-label', `🏆 ${t.label}`));
      const sub = t.livery
        ? `Livrée « ${t.livery.name} »${t.active ? '' : ' · expirée'}`
        : t.active
          ? 'Titre honorifique'
          : 'expiré';
      body.appendChild(el('div', 'title-sub', sub));
      card.appendChild(body);
      list.appendChild(card);
    }
    container.appendChild(list);
  }

  if (!liveryControls) return;

  // The wardrobe: every distinct livery unlocked by a title, plus plain colour.
  const owned = dedupLiveries(profile.titles.map((t) => t.livery));
  container.appendChild(el('h3', undefined, 'Livrées débloquées'));
  const wardrobe = el('div', 'livery-wardrobe');
  const wornName = liveryControls.worn?.name ?? null;

  const plain = liveryOption(
    carSwatch(liveryControls.baseDesign, 56),
    'Ma voiture',
    wornName === null,
    () => liveryControls.onEquip(null),
  );
  wardrobe.appendChild(plain);

  for (const liv of owned) {
    const opt = liveryOption(
      carSwatch(applyLiveryToCarConfig(liveryControls.baseDesign, liv), 56),
      liv.name,
      wornName === liv.name,
      () => liveryControls.onEquip(liv),
    );
    wardrobe.appendChild(opt);
  }
  container.appendChild(wardrobe);
}

function liveryOption(
  swatch: HTMLElement,
  name: string,
  active: boolean,
  onEquip: () => void,
): HTMLElement {
  const card = el('div', `livery-option${active ? ' active' : ''}`);
  card.appendChild(swatch);
  card.appendChild(el('div', 'livery-name', name));
  const btn = el('button', 'livery-equip', active ? '✓ Équipée' : 'Équiper');
  btn.disabled = active;
  btn.addEventListener('click', onEquip);
  card.appendChild(btn);
  return card;
}

function dedupLiveries(liveries: (Livery | null)[]): Livery[] {
  const seen = new Set<string>();
  const out: Livery[] = [];
  for (const l of liveries) {
    if (!l || seen.has(l.name)) continue;
    seen.add(l.name);
    out.push(l);
  }
  return out;
}

export function renderProfile(
  container: HTMLElement,
  profile: ProfileData,
  cb: StatsCallbacks,
  liveryControls?: LiveryControls,
): void {
  container.replaceChildren();

  const header = el('div', 'stats-header');
  const swatch = el('span', 'stats-swatch');
  swatch.style.backgroundColor = profile.account.color;
  const title = el('h2', undefined, profile.account.pseudo);
  header.append(swatch, title);
  if (profile.account.isAdmin) header.appendChild(el('span', 'badge badge-admin', 'admin'));
  container.appendChild(header);
  container.appendChild(
    el('div', 'stats-sub', `Membre depuis le ${formatDate(profile.account.createdAt)}`),
  );

  // Totals.
  const totals = el('div', 'stats-tiles');
  const tiles: [string, number][] = [
    ['Courses', profile.totals.races],
    ['Victoires', profile.totals.wins],
    ['Podiums', profile.totals.podiums],
    ['Records du tour', profile.totals.bestLaps],
  ];
  for (const [label, value] of tiles) {
    const tile = el('div', 'stats-tile');
    tile.append(el('div', 'stats-tile-value', String(value)), el('div', 'stats-tile-label', label));
    totals.appendChild(tile);
  }
  container.appendChild(totals);

  renderTitles(container, profile, liveryControls);

  // Personal bests per circuit.
  container.appendChild(el('h3', undefined, 'Meilleurs tours par circuit'));
  if (profile.personalBests.length === 0) {
    container.appendChild(el('div', 'stats-empty', 'Aucun tour valide pour l’instant.'));
  } else {
    const rows = profile.personalBests.map((pb) => {
      const name = el('button', 'link-btn', pb.circuitName);
      name.addEventListener('click', () => cb.onOpenCircuit(pb.circuitId));
      return [
        name,
        formatLapTime(pb.bestLapMs),
        pb.recordMs !== null ? formatLapTime(pb.recordMs) : '—',
        formatGap(pb.gapMs),
      ];
    });
    container.appendChild(table(['Circuit', 'Votre record', 'Record absolu', 'Écart'], rows));
  }

  // Best-lap progression charts.
  if (profile.progressions.length > 0) {
    container.appendChild(el('h3', undefined, 'Progression du meilleur tour'));
    for (const prog of profile.progressions) {
      const block = el('div', 'stats-chart-block');
      block.appendChild(el('div', 'stats-chart-title', prog.circuitName));
      const canvas = el('canvas', 'stats-chart');
      block.appendChild(canvas);
      container.appendChild(block);
      // Draw after layout so clientWidth is known.
      requestAnimationFrame(() => {
        drawLapChart(
          canvas,
          [
            {
              label: prog.circuitName,
              color: '#ffb02e',
              points: prog.points.map((p, i) => ({ x: i, y: p.timeMs })),
            },
          ],
          formatLapTime,
        );
      });
    }
  }

  // Cumulative points per league season (higher is better → chart climbs).
  if (profile.seasonPoints.length > 0) {
    container.appendChild(el('h3', undefined, 'Points par saison'));
    for (const series of profile.seasonPoints) {
      const block = el('div', 'stats-chart-block');
      block.appendChild(
        el('div', 'stats-chart-title', `${series.leagueName} — saison ${series.seasonNumber}`),
      );
      const canvas = el('canvas', 'stats-chart');
      block.appendChild(canvas);
      container.appendChild(block);
      requestAnimationFrame(() => {
        drawLapChart(
          canvas,
          [
            {
              label: series.leagueName,
              color: '#2ed573',
              points: series.points.map((p) => ({ x: p.race, y: p.cumulative })),
            },
          ],
          (v) => `${Math.round(v)} pts`,
          { invertY: false },
        );
      });
    }
  }

  // Race history.
  container.appendChild(el('h3', undefined, 'Historique des courses'));
  if (profile.raceHistory.length === 0) {
    container.appendChild(el('div', 'stats-empty', 'Aucune course disputée.'));
  } else {
    const rows = profile.raceHistory.map((r) => {
      const name = el('button', 'link-btn', r.circuitName);
      name.addEventListener('click', () => cb.onOpenCircuit(r.circuitId));
      const place = r.finished ? `${r.finishPos}/${r.players}` : `abandon`;
      return [
        formatDate(r.date),
        name,
        `${r.laps} t`,
        place,
        r.totalTimeMs !== null ? formatLapTime(r.totalTimeMs) : '—',
        r.bestLapMs !== null ? formatLapTime(r.bestLapMs) : '—',
      ];
    });
    container.appendChild(
      table(['Date', 'Circuit', 'Tours', 'Place', 'Temps total', 'Meilleur tour'], rows),
    );
  }
}

export function renderCircuitStats(
  container: HTMLElement,
  stats: CircuitStats,
  cb: StatsCallbacks,
): void {
  container.replaceChildren();

  const header = el('div', 'stats-header');
  header.appendChild(el('h2', undefined, stats.name));
  header.appendChild(
    el(
      'span',
      `circuit-status${stats.status === 'validated' ? ' validated' : ''}`,
      stats.status === 'validated' ? 'validé' : 'brouillon',
    ),
  );
  container.appendChild(header);
  container.appendChild(
    el('div', 'stats-sub', `${(stats.lengthPx / 100 / 10).toFixed(1)} km fictifs`),
  );

  // Records.
  container.appendChild(el('h3', undefined, 'Records'));
  const recRows: (string | HTMLElement)[][] = [];
  if (stats.lapRecord) {
    const who = el('button', 'link-btn', stats.lapRecord.pseudo);
    who.addEventListener('click', () => cb.onOpenProfile(stats.lapRecord!.pseudo));
    recRows.push([
      'Meilleur tour',
      formatLapTime(stats.lapRecord.timeMs),
      who,
      formatDate(stats.lapRecord.date),
    ]);
  }
  for (const rr of stats.raceRecords) {
    const who = el('button', 'link-btn', rr.pseudo);
    who.addEventListener('click', () => cb.onOpenProfile(rr.pseudo));
    recRows.push([`Course ${rr.laps} tours`, formatLapTime(rr.timeMs), who, formatDate(rr.date)]);
  }
  if (recRows.length === 0) {
    container.appendChild(el('div', 'stats-empty', 'Aucun record encore établi.'));
  } else {
    container.appendChild(table(['Type', 'Temps', 'Détenteur', 'Date'], recRows));
  }

  // Top 10 laps.
  container.appendChild(el('h3', undefined, 'Top 10 des meilleurs tours'));
  if (stats.topLaps.length === 0) {
    container.appendChild(el('div', 'stats-empty', 'Aucun tour valide.'));
  } else {
    const rows = stats.topLaps.map((l) => {
      const who = el('button', 'link-btn', l.pseudo);
      who.addEventListener('click', () => cb.onOpenProfile(l.pseudo));
      return [`${l.pos}`, who, formatLapTime(l.timeMs), formatDate(l.date)];
    });
    container.appendChild(table(['#', 'Pilote', 'Temps', 'Date'], rows));
  }

  // Race history.
  container.appendChild(el('h3', undefined, 'Courses disputées'));
  if (stats.raceHistory.length === 0) {
    container.appendChild(el('div', 'stats-empty', 'Aucune course sur ce circuit.'));
  } else {
    const rows = stats.raceHistory.map((r) => {
      const who = r.winner ? el('button', 'link-btn', r.winner) : el('span', undefined, '—');
      if (r.winner) who.addEventListener('click', () => cb.onOpenProfile(r.winner as string));
      return [
        formatDate(r.date),
        `${r.laps} t`,
        `${r.players}`,
        who,
        r.winnerTimeMs !== null ? formatLapTime(r.winnerTimeMs) : '—',
      ];
    });
    container.appendChild(table(['Date', 'Tours', 'Pilotes', 'Vainqueur', 'Temps'], rows));
  }
}
